import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { CurrencyModel } from 'src/app/models/management/currency.model';
import { CurrencyService } from 'src/app/services/management/currency.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-currency',
  templateUrl: './register-currency.component.html',
  styleUrls: ['./register-currency.component.scss'],
  standalone: false
})
export class RegisterCurrencyComponent implements OnInit {

  currencyId: number = 0;
  currencyContract: CurrencyModel = new CurrencyModel();
  disabledForm: boolean = false;
  formCurrency: FormGroup = new FormGroup({});
  permissionUpdatable: boolean = false;
  subscription: Subscription;
  url: string = 'currency';

  constructor(public config: DynamicDialogConfig,
    private currencyService: CurrencyService,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new CurrencyModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.currencyContract = inputs.currencyContract;
      this.permissionUpdatable = inputs.permissionUpdatable;
    });
    this.createNewRegister();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async changeCurrency(code: string) {
    if (!code?.trim()) {
      this.createNewRegister();
      return;
    }

    await this.getCurrencyByCode(code.trim());
  }

  createNewRegister() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('warning', `El usuario no tiene permisos para crear monedas.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    this.validateForm(this.currencyContract);
  }

  async createCurrency(modelCurrency: CurrencyModel) {
    try {
      const response = await firstValueFrom(this.currencyService.createCurrency(modelCurrency));
      this.currencyId = response.currencyId;
      this.currencyContract = response;
      this.validateForm(this.currencyContract);
      this.toastHandlerService.showToast('success', `La moneda fue creada exitosamente.`);
    } catch (error) {
      console.error('Error al crear la moneda.', error);
    }
  }

  async getCurrencyByCode(code: string) {
    try {
      const response = await firstValueFrom(this.currencyService.getCurrencyByCode(code));
      this.currencyId = response.currencyId;
      this.currencyContract = response;
      this.validateForm(this.currencyContract);
    } catch (error) {
      console.error('Error al obtener la moneda por codigo.', error)
    }
  }

  onReloadEmit() {
    const output$ = this.config.data?.output$;
    output$?.next({ type: 'onReload', payload: true });
  }

  async send() {
    this.validateFormControls();
    if (!this.formCurrency.valid) {
      this.showValidationMessages();
      return;
    }
    const modelCurrency = this.formCurrency.getRawValue() as CurrencyModel;
    if (this.currencyId == 0) {
      await this.createCurrency(modelCurrency);
    } else {
      await this.updateCurrency(modelCurrency);
    }
    this.onReloadEmit();
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      code: {
        required: 'Debe ingresar el Código de la moneda.',
        maxlength: 'El Código de la moneda no puede tener más de 3 caracteres.'
      },
      name: {
        required: 'Debe ingresar el Nombre de la moneda.',
        maxlength: 'El Nombre de la moneda no puede tener más de 50 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formCurrency.get(controlName);
      if (control?.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async updateCurrency(modelCurrency: CurrencyModel) {
    this.currencyContract.name = modelCurrency.name;
    try {
      const response = await firstValueFrom(this.currencyService.updateCurrency(this.currencyContract));
      this.currencyContract = response;
      this.validateForm(this.currencyContract);
      this.toastHandlerService.showToast('success', `La moneda fue modificada exitosamente.`);
    } catch (error) {
      console.error('Error al modificar la moneda.', error);
    }
  }

  updateFormPermision() {
    if (this.permissionUpdatable) {
      this.disabledForm = false;
      setTimeout(() => this.formCurrency.enable());
    } else {
      this.disabledForm = true;
      setTimeout(() => this.formCurrency.disable());
    }
  }

  validateForm(currency: CurrencyModel) {
    this.formCurrency = this.formBuilder.group({
      code: [currency.code, [Validators.required, Validators.maxLength(3)]],
      name: [currency.name, [Validators.required, Validators.maxLength(50)]]
    });
    this.updateFormPermision();
  }

  validateFormControls() {
    Object.keys(this.formCurrency.controls).forEach((key) => {
      const control = this.formCurrency.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
